import { z } from "zod";

/**
 * WHERE THEY ARE TRAINING — the app's half of the engine's `Room`.
 *
 * MIRRORS TWO TABLES:
 *   `db/schemas/tables/gyms.sql`          → `gymSchema`  (the three SPACE flags + the listing)
 *   `db/schemas/tables/gym_equipment.sql` → `gymEquipmentSchema` (one row per item, per SIZE)
 *
 * THE SPLIT IS THE VOCABULARY'S OWN LINE. Properties of the ROOM live on the gym; everything
 * the equipment registry names lives in its own row. `EquipmentCategory` carries a `space`
 * bucket that no equipment row ever uses, which is the engine drawing the same line.
 */

/**
 * THE MOST CONSEQUENTIAL FIELD ON A GYM.
 *
 *   complete — if it is not listed it does not exist. A box that has been inventoried.
 *   partial  — what is listed is CONFIRMED; what is not is UNKNOWN, not absent. A member who
 *              ticked four boxes on a signup screen and closed the app.
 *
 * The strict reading is the default ON PURPOSE, in the engine and in the column: the
 * permissive one invents kit the athlete may not own, and a card printing 47.5 kg for a room
 * that owns no 1.25s is the output the inventory layer exists to prevent.
 */
export const ROOM_LISTINGS = ["complete", "partial"] as const;
export const roomListingSchema = z.enum(ROOM_LISTINGS);
export type RoomListing = (typeof ROOM_LISTINGS)[number];

/**
 * WHAT AN ABSENCE MEANS, AND WHY IT NEEDS A WORD.
 *
 * On a `partial` listing "no row" means UNKNOWN — the engine assumes a non-rack item is
 * probably there. `absent` is the athlete saying so out loud, which is a different fact and
 * the only way a partial room can rule something out without being upgraded to `complete`.
 */
export const EQUIPMENT_STATES = ["present", "absent"] as const;
export const equipmentStateSchema = z.enum(EQUIPMENT_STATES);
export type EquipmentState = (typeof EQUIPMENT_STATES)[number];

/**
 * ONE PIECE OF KIT, AND ONE SIZE OF IT.
 *
 * ONE ROW PER SIZE IS WHAT THE ENGINE ACTUALLY READS: `Room.plates`, `Room.kb`, `Room.db` and
 * `Room.medBall` are LISTS OF WEIGHTS. A min/max/step range cannot express a rack with a gap
 * — the 20 kg bell somebody walked off with — and would have to be expanded into a list on
 * every read anyway.
 *
 * `item` IS NOT AN ENUM HERE, because `EquipmentId` is an open alias and the table carries no
 * check either. It also holds one id the registry does not: `plate`. A plate is a rack the
 * engine reads and not a thing any movement can REQUIRE, so it has no registry row.
 */
export const gymEquipmentSchema = z
  .object({
    id: z.uuid(),
    item: z.string().min(1),
    state: equipmentStateSchema,
    /**
     * The size, for kit that comes in sizes — a plate, a bell, a dumbbell, a med ball, and
     * the BAR'S OWN WEIGHT. Null for anything that is a yes or a no.
     */
    weightKg: z.number().positive().nullable(),
    /** Null means unknown or uncounted, which is what the engine assumes today. */
    quantity: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .refine((v) => v.state === "present" || (v.weightKg === null && v.quantity === null), {
    message: "an-absence-carries-no-size",
    path: ["state"],
  });

export type GymEquipmentDTO = z.infer<typeof gymEquipmentSchema>;

/**
 * THE ROOM ITSELF. The three space flags are the engine's `openFloor`, `outdoor` and `wall`
 * — room to travel in, a route outside, and a wall to kick up on or throw at.
 */
export const gymSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(80),
  isDefault: z.boolean(),
  listing: roomListingSchema,
  openFloor: z.boolean(),
  outdoor: z.boolean(),
  wall: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type GymDTO = z.infer<typeof gymSchema>;

/**
 * A GYM WITH ITS KIT — a genuinely different field set from `gymSchema`, not a description of
 * one, which is why it is a second schema rather than an optional array.
 *
 * The picker lists gyms and shows a name; the editor shows a rack. Sending every plate of
 * every gym to a picker is the payload the split exists to avoid, and a merely-optional
 * `equipment` would leave a client inferring which it got from whether the field is there.
 */
export const gymWithEquipmentSchema = gymSchema.extend({
  equipment: z.array(gymEquipmentSchema),
});

export type GymWithEquipmentDTO = z.infer<typeof gymWithEquipmentSchema>;

/**
 * THE WRITABLE FIELDS, WITH NO DEFAULTS ON THEM. Read the note on `updateGymSchema` before
 * moving a `.default()` up here — it is the reason this object exists separately at all.
 */
const gymWritable = z.object({
  name: z.string().min(1).max(80),
  listing: roomListingSchema,
  isDefault: z.boolean(),
  openFloor: z.boolean(),
  outdoor: z.boolean(),
  wall: z.boolean(),
});

/**
 * Creating carries the defaults, and `listing` is defaulted to the STRICT reading exactly as
 * the column is, so a screen that never asks behaves like the engine's own fallback instead
 * of quietly assuming a bench.
 */
export const createGymSchema = gymWritable.extend({
  listing: roomListingSchema.default("complete"),
  isDefault: z.boolean().default(false),
  openFloor: z.boolean().default(false),
  outdoor: z.boolean().default(false),
  wall: z.boolean().default(false),
});

export type CreateGymPayload = z.infer<typeof createGymSchema>;

/**
 * ⚠️ BUILT FROM THE BARE SHAPE, NOT FROM `createGymSchema.partial()`, AND THAT IS LOAD-BEARING.
 *
 * `.partial()` makes a key optional; it does NOT remove a `.default()` underneath it. So
 * `createGymSchema.partial().parse({ name: "New Name" })` returns every other field at its
 * default — and a handler applying that parsed payload to an UPDATE silently resets the
 * listing and all three space flags on a gym whose name was the only thing anyone touched.
 * The caller sent one field; the contract handed back six.
 *
 * A partial update must return ONLY what the caller actually sent, so it is derived from the
 * shape that never had defaults. `shared/contracts/__tests__/write-payloads.test.ts` holds
 * this — it is invisible to types, since both spellings infer the same optional fields.
 */
export const updateGymSchema = gymWritable.partial();

export type UpdateGymPayload = z.infer<typeof updateGymSchema>;

/**
 * An upsert against `unique nulls not distinct (gym_id, item, weight_kg)`: adding the same
 * bell twice is adding it once, and the table treats two null sizes as the same value so a
 * second `bench` row collides with the first rather than landing silently.
 */
export const setGymEquipmentSchema = z
  .object({
    item: z.string().min(1).max(80),
    state: equipmentStateSchema.default("present"),
    weightKg: z.number().positive().max(500).nullable().optional(),
    quantity: z.number().int().positive().max(1000).nullable().optional(),
  })
  .refine((v) => v.state !== "absent" || (v.weightKg == null && v.quantity == null), {
    message: "an-absence-carries-no-size",
    path: ["state"],
  });

export type SetGymEquipmentPayload = z.infer<typeof setGymEquipmentSchema>;

/**
 * THE PAGE SIZE FOR A GYM LIST, and the ceiling on one.
 *
 * A person has one gym, sometimes three, never fifty — so the default is generous enough that
 * the list is one request in practice, and the max is the bound that keeps one request from
 * becoming the outage. Keyset, never OFFSET: a list is scrolled while rows are inserted.
 */
export const listGymsSchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type ListGymsQuery = z.infer<typeof listGymsSchema>;

export const gymListSchema = z.object({
  items: z.array(gymSchema),
  nextCursor: z.string().nullable(),
});

export type GymListDTO = z.infer<typeof gymListSchema>;

/** The id in `/v1/gyms/:id`, shared so the app rejects a malformed one before the round trip. */
export const gymIdSchema = z.object({ id: z.uuid() });

export type GymIdParam = z.infer<typeof gymIdSchema>;

/** The id in `/v1/gyms/:id/equipment/:equipmentId`. */
export const gymEquipmentIdSchema = z.object({ id: z.uuid(), equipmentId: z.uuid() });

export type GymEquipmentIdParam = z.infer<typeof gymEquipmentIdSchema>;
